import { useEffect, useMemo, useState } from 'react';

import {
  Sender,
  MessageType,
} from '@wix/ambassador-innovation-widget-v1-message/types';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';

import {
  addMessage,
  AddMessagePayload,
  selectConversationsQuota,
  selectMessages,
  selectOneChatComposed,
  selectOneChatSettings,
} from '@/features';
import { useAppDispatch, useAppSelector } from '@/store';
import { EXPERIMENTS } from '@/constants';

import { useAppExperiments } from 'AiAssistantWidget/experiments';

export const useOneChatIntroSequence = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { isEditor } = useEnvironment();
  const { experiments } = useAppExperiments();
  const [isSequenceFulfilled, setIsSequenceFulfilled] = useState(true);
  const settingsLoaded = useAppSelector(selectOneChatSettings.isLoaded);
  const messagesLoaded = useAppSelector(selectMessages.isLoaded);
  const quotaLoaded = useAppSelector(selectConversationsQuota.isLoaded);

  const aiIntroMessage = useAppSelector(selectOneChatComposed.aiIntroMessage);
  const manualIntroMessage = useAppSelector(
    selectOneChatComposed.manualIntroMessage,
  );
  const aliLegalMessage = useAppSelector(selectOneChatComposed.aiLegalMessage);
  const offlineMessage = useAppSelector(
    selectOneChatComposed.offlineIntroMessage,
  );

  const shouldShowContactFormInIntroSequence = useAppSelector(
    selectOneChatComposed.shouldShowContactFormInIntroSequence,
  );

  const aiSuggestedQuestions = useAppSelector(
    selectOneChatComposed.aiSuggestions,
  );
  const aiSuggestionsIntroMessage = useAppSelector(
    selectOneChatComposed.aiSuggestionsIntroMessage,
  );
  const noIntroInHistory = useAppSelector(selectMessages.shouldSendIntro);
  const noLegalInHistory = useAppSelector(selectMessages.shouldSendLegal);
  const noOfflineMessageInHistory = useAppSelector(
    selectMessages.shouldSendOfflineMessage,
  );

  const shouldFullfillSequence = useMemo(
    () => settingsLoaded && messagesLoaded && quotaLoaded,
    [settingsLoaded, messagesLoaded, quotaLoaded],
  );

  const shouldSendAiIntro = useMemo(
    () => Boolean(noIntroInHistory && aiIntroMessage),
    [noIntroInHistory, aiIntroMessage],
  );

  const shouldSendManualIntro = useMemo(
    () => Boolean(noIntroInHistory && manualIntroMessage),
    [noIntroInHistory, manualIntroMessage],
  );

  const shouldSendAiLegal = useMemo(
    () => Boolean(noLegalInHistory && aliLegalMessage),
    [noLegalInHistory, aliLegalMessage],
  );

  const shouldSendOfflineMessage = useMemo(
    () => Boolean(noOfflineMessageInHistory && offlineMessage),
    [noOfflineMessageInHistory, offlineMessage],
  );

  const isSuggestedIntroQuestionsEnabled = experiments.enabled(
    EXPERIMENTS.SUGGESTED_QUESTIONS,
  );

  const shouldSendSuggestions = useAppSelector(
    selectOneChatComposed.shouldSendSuggestions,
  );

  const payload = useMemo(() => {
    const result: AddMessagePayload[] = [];
    if (shouldSendManualIntro) {
      result.push({
        body: { mainText: manualIntroMessage as string },
        sender: Sender.ASSISTANT,
        messageType: MessageType.INTRO,
      });
    }
    if (shouldSendAiIntro) {
      result.push({
        body: { mainText: aiIntroMessage as string },
        sender: Sender.ASSISTANT,
        messageType: MessageType.INTRO,
      });
    }
    if (shouldSendAiLegal) {
      result.push({
        body: { mainText: aliLegalMessage as string },
        sender: Sender.ASSISTANT,
        messageType: MessageType.LEGAL,
      });
      result.push({
        body: { mainText: t('app.widget.chat.inboxLegalMessage') },
        sender: Sender.SYSTEM,
        messageType: MessageType.SITE_OWNER_NOTIFICATION,
      });
    }
    if (shouldSendSuggestions && isSuggestedIntroQuestionsEnabled) {
      const additionalData =
        aiSuggestedQuestions?.map(({ text: title }) => ({
          type: 'suggested_question',
          title,
        })) ?? [];
      const mainText = aiSuggestionsIntroMessage || '';

      result.push({
        body: {
          mainText,
          additionalData,
        },
        sender: Sender.ASSISTANT,
        messageType: MessageType.SUGGESTED_QUESTION,
      });
    }

    if (shouldSendOfflineMessage) {
      result.push({
        body: { mainText: offlineMessage as string },
        sender: Sender.ASSISTANT,
        messageType: MessageType.OFFLINE,
      });
    }

    if (shouldShowContactFormInIntroSequence) {
      result.push({
        body: { mainText: t('app.widget.form.beforeText') },
        sender: Sender.ASSISTANT,
        messageType: MessageType.CONTACT_FORM,
      });
    }
    return result;
  }, [
    isSuggestedIntroQuestionsEnabled,
    shouldSendManualIntro,
    shouldSendAiIntro,
    shouldSendAiLegal,
    shouldShowContactFormInIntroSequence,
    shouldSendOfflineMessage,
    shouldSendSuggestions,
    aiSuggestedQuestions,
    aiSuggestionsIntroMessage,
    offlineMessage,
    manualIntroMessage,
    aiIntroMessage,
    aliLegalMessage,
    t,
  ]);

  useEffect(() => {
    if (shouldFullfillSequence && payload.length && !isEditor) {
      setIsSequenceFulfilled(false);
      dispatch(addMessage(payload)).finally(() => {
        setIsSequenceFulfilled(true);
      });
    }
  }, [shouldFullfillSequence, payload, dispatch, isEditor]);

  return {
    isSequenceFulfilled,
  };
};
